import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Command, ArrowRight, ArrowLeft, Mail } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
    const { login } = useAuth();
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    // Forgot password state
    const [showForgot, setShowForgot] = useState(false);
    const [forgotEmail, setForgotEmail] = useState("");
    const [forgotLoading, setForgotLoading] = useState(false);
    const [forgotSent, setForgotSent] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await login(email, password);
            nav("/dashboard");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    const onForgotSubmit = async (e) => {
        e.preventDefault();
        setForgotLoading(true);
        try {
            await api.post("/auth/forgot-password", { email: forgotEmail });
            setForgotSent(true);
            toast.success("If that email is registered, you'll receive a reset link shortly.");
        } catch (err) {
            if (err.response?.status === 503) {
                toast.error("Email is not yet configured. Please contact support.");
            } else {
                toast.error(err.response?.data?.detail || "Something went wrong");
            }
        } finally {
            setForgotLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2">
            <div className="flex items-center justify-center p-8" data-testid="login-page">
                <div className="w-full max-w-sm animate-fade-up">
                    <Link to="/" className="flex items-center gap-2 mb-10" data-testid="brand-login">
                        <div className="w-9 h-9 bg-foreground text-background flex items-center justify-center rounded-md">
                            <Command className="w-4 h-4" />
                        </div>
                        <div>
                            <div className="font-display font-bold tracking-tight">Closvio</div>
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">M&A Platform</div>
                        </div>
                    </Link>

                    <h1 className="text-3xl font-display font-bold tracking-tight mb-1">Welcome back</h1>
                    <p className="text-sm text-muted-foreground mb-8">Sign in to your dealmaking workspace.</p>

                    <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
                        <div className="space-y-2">
                            <Label htmlFor="email" className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
                            <Input
                                id="email" type="email" autoComplete="email" required
                                value={email} onChange={(e) => setEmail(e.target.value)}
                                data-testid="login-email-input"
                                placeholder="you@firm.com"
                            />
                        </div>
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <Label htmlFor="password" className="text-xs uppercase tracking-wider text-muted-foreground">Password</Label>
                                <button
                                    type="button"
                                    onClick={() => { setShowForgot(true); setForgotEmail(email); }}
                                    className="text-xs text-muted-foreground hover:text-gold transition-colors"
                                    data-testid="forgot-password-link"
                                >
                                    Forgot password?
                                </button>
                            </div>
                            <Input
                                id="password" type="password" autoComplete="current-password" required
                                value={password} onChange={(e) => setPassword(e.target.value)}
                                data-testid="login-password-input"
                                placeholder="••••••••"
                            />
                        </div>
                        <Button
                            type="submit" disabled={loading}
                            className="w-full bg-foreground text-background hover:bg-neutral-800 hover:text-white"
                            data-testid="login-submit-btn"
                        >
                            {loading ? "Signing in…" : (<>Sign in <ArrowRight className="w-4 h-4 ml-1.5" /></>)}
                        </Button>
                    </form>

                    {/* Forgot Password Inline Panel */}
                    {showForgot && (
                        <div className="mt-6 p-4 border border-border rounded-md bg-muted/30 animate-fade-up" data-testid="forgot-password-panel">
                            {forgotSent ? (
                                <div className="text-center">
                                    <Mail className="w-8 h-8 text-gold mx-auto mb-2" />
                                    <p className="text-sm font-medium mb-1">Check your email</p>
                                    <p className="text-xs text-muted-foreground">
                                        If that email is registered, you'll receive a reset link shortly.
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => { setShowForgot(false); setForgotSent(false); }}
                                        className="mt-3 text-xs text-gold hover:underline"
                                    >
                                        Back to login
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <div className="flex items-center gap-2 mb-3">
                                        <button
                                            type="button"
                                            onClick={() => setShowForgot(false)}
                                            className="text-muted-foreground hover:text-foreground"
                                        >
                                            <ArrowLeft className="w-4 h-4" />
                                        </button>
                                        <span className="text-sm font-medium">Reset password</span>
                                    </div>
                                    <form onSubmit={onForgotSubmit} className="space-y-3">
                                        <Input
                                            type="email"
                                            placeholder="Enter your email"
                                            value={forgotEmail}
                                            onChange={(e) => setForgotEmail(e.target.value)}
                                            required
                                            data-testid="forgot-email-input"
                                        />
                                        <Button
                                            type="submit"
                                            disabled={forgotLoading}
                                            className="w-full"
                                            variant="outline"
                                            data-testid="forgot-submit-btn"
                                        >
                                            {forgotLoading ? "Sending…" : "Send reset link"}
                                        </Button>
                                    </form>
                                </>
                            )}
                        </div>
                    )}

                    <p className="text-sm text-muted-foreground mt-6">
                        New firm?{" "}
                        <Link to="/register" className="text-foreground font-medium underline underline-offset-4 hover:no-underline" data-testid="go-register-link">
                            Create an account
                        </Link>
                    </p>
                </div>
            </div>

            <div className="hidden lg:block relative bg-navy overflow-hidden">
                <div className="absolute inset-0 grid-bg opacity-30" />
                <div className="absolute top-10 right-10 w-64 h-64 rounded-full bg-gold/10 blur-3xl" />
                <div className="absolute inset-0 flex flex-col justify-end p-12 text-white">
                    <div className="max-w-lg">
                        <div className="text-xs uppercase tracking-[0.2em] text-gold mb-4">Built for M&A desks</div>
                        <h2 className="font-display text-4xl font-bold tracking-tight leading-tight mb-4">
                            Replace your spreadsheet.<br />Close more deals.
                        </h2>
                        <p className="text-sm text-white/60 leading-relaxed">
                            A deal-centric pipeline purpose-built for M&A brokers, corporate finance advisors, and boutique investment banks.
                        </p>
                        <div className="mt-10 grid grid-cols-3 gap-6 border-t border-gold/30 pt-8">
                            <div>
                                <div className="font-mono text-2xl text-gold">10</div>
                                <div className="text-[11px] uppercase tracking-wider text-white/60 mt-1">Pipeline stages</div>
                            </div>
                            <div>
                                <div className="font-mono text-2xl text-gold">AI</div>
                                <div className="text-[11px] uppercase tracking-wider text-white/60 mt-1">Deal insights</div>
                            </div>
                            <div>
                                <div className="font-mono text-2xl text-gold">8+</div>
                                <div className="text-[11px] uppercase tracking-wider text-white/60 mt-1">Currencies</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
